import degreeType from "../types/degreeType";

const ALL_EDUCATION_TYPES: degreeType[] = ["high", "additional", "postgraduate"];
const DEFAULT_NOTIFICATION_TYPES: degreeType[] = ["high", "additional"];

type DomainAdmissionConfig = {
  applications: degreeType[];
  notifications: degreeType[];
  registrationUrl: string;
  profile?: {
    isSnilsOptionalForRfPassport?: boolean;
  };
};

const DEFAULT_REGISTRATION_URL = "https://start.instudy.online/registration/";
const REVERSE_SOURCE_DOMAIN = "ad5566172e2b57e7f917020bfcadf503.inpsycho.ru";
const PRIEM_INSTUDY_DOMAIN = "priem.instudy.online";

/** reverse из user.info: runtime-флаг, на inpsycho показываем логику priem.instudy.online */
let reverseMode = false;

const EPGU_CODE_EMAIL_REGEXP = /^\d+$/;

const DEFAULT_DOMAIN_CONFIG: DomainAdmissionConfig = {
  applications: ALL_EDUCATION_TYPES,
  notifications: DEFAULT_NOTIFICATION_TYPES,
  registrationUrl: DEFAULT_REGISTRATION_URL,
};

const DOMAIN_CONFIGS: Record<string, DomainAdmissionConfig> = {
  [REVERSE_SOURCE_DOMAIN]: {
    applications: ["high"],
    notifications: ["high"],
    registrationUrl: "https://start.instudy.online/reg/",
    profile: {
      isSnilsOptionalForRfPassport: true,
    },
  },
  [PRIEM_INSTUDY_DOMAIN]: {
    applications: ["additional"],
    notifications: ["high", "additional"],
    registrationUrl: DEFAULT_REGISTRATION_URL,
  },
};

const isReverseMode = (): boolean =>
  window.location.hostname === REVERSE_SOURCE_DOMAIN && reverseMode;

export const isPriemInstudyDomain = (): boolean =>
  window.location.hostname === PRIEM_INSTUDY_DOMAIN || isReverseMode();

export const setReverseDomainMode = (reverse: boolean): void => {
  reverseMode = reverse && window.location.hostname === REVERSE_SOURCE_DOMAIN;
};

const isPriemInstudyHost = (): boolean => window.location.hostname === PRIEM_INSTUDY_DOMAIN;

const isEpguCodeEmail = (email?: string | null): boolean =>
  typeof email === "string" && EPGU_CODE_EMAIL_REGEXP.test(email.trim());

const getPriemAuthorizedUserEmail = (): string | null | undefined => {
  // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
  const store = require("../../store/store").default;
  return store.getState().coreState.accountState.user?.email;
};

const shouldHideDpoOnPriem = (): boolean =>
  isPriemInstudyHost() && isEpguCodeEmail(getPriemAuthorizedUserEmail());

export const APPLICATION_ROUTE_PATHS: Record<
  degreeType,
  { additionalData: string; applications: string }
> = {
  high: {
    additionalData: "additional-high-data",
    applications: "high-education",
  },
  additional: {
    additionalData: "additional-ae-data",
    applications: "additional-education",
  },
  postgraduate: {
    additionalData: "additional-postgraduate-data",
    applications: "postgraduate-education",
  },
};

export const NOTIFICATION_ROUTE_PATHS: Record<degreeType, string> = {
  high: "high-education",
  additional: "additional-education",
  postgraduate: "postgraduate-education",
};

export const ENTRANCE_EXAMS_ROUTE_PATH = "entrance-exams";

const getDomainConfig = (): DomainAdmissionConfig => {
  if (isReverseMode()) {
    return DOMAIN_CONFIGS[PRIEM_INSTUDY_DOMAIN];
  }

  return DOMAIN_CONFIGS[window.location.hostname] ?? DEFAULT_DOMAIN_CONFIG;
};

export const getVisibleEducationTypes = (): degreeType[] => {
  const types = getDomainConfig().applications;
  if (!isPriemInstudyHost()) {
    return types;
  }

  return shouldHideDpoOnPriem() ? types.filter((type) => type !== "additional") : types;
};

export const getVisibleNotificationEducationTypes = (): degreeType[] => {
  const types = getDomainConfig().notifications;
  if (!isPriemInstudyHost()) {
    return types;
  }

  return shouldHideDpoOnPriem() ? types.filter((type) => type !== "additional") : types;
};

export const isEducationTypeVisible = (educationType: degreeType): boolean =>
  getVisibleEducationTypes().includes(educationType);

export const isNotificationEducationTypeVisible = (educationType: degreeType): boolean =>
  getVisibleNotificationEducationTypes().includes(educationType);

export const hasVisibleApplicationTypes = (): boolean => {
  if (!isPriemInstudyHost()) {
    return getVisibleEducationTypes().length > 0;
  }

  return getDomainConfig().applications.length > 0;
};

export const isSnilsOptionalForRfPassport = (): boolean =>
  getDomainConfig().profile?.isSnilsOptionalForRfPassport ?? false;

export const getRegistrationUrl = (): string => getDomainConfig().registrationUrl;

export const getDefaultNotificationPath = (
  hasCompletedHighEducationApplications: boolean,
  hasCompletedAdditionalEducationApplications: boolean,
  ss?: string
): string => {
  const notificationTypes = getVisibleNotificationEducationTypes();
  const pathByType: Record<degreeType, string> = {
    high: `/notifications/${NOTIFICATION_ROUTE_PATHS.high}`,
    additional: `/notifications/${NOTIFICATION_ROUTE_PATHS.additional}`,
    postgraduate: `/notifications/${NOTIFICATION_ROUTE_PATHS.postgraduate}`,
  };

  if (ss === "1" && notificationTypes.includes("high")) {
    return pathByType.high;
  }

  if (notificationTypes.includes("high") && hasCompletedHighEducationApplications) {
    return pathByType.high;
  }

  if (notificationTypes.includes("additional") && hasCompletedAdditionalEducationApplications) {
    return pathByType.additional;
  }

  return pathByType[notificationTypes[0]];
};

export const hasCompletedVisibleNotificationApplications = (
  hasCompletedHighEducationApplications: boolean,
  hasCompletedAdditionalEducationApplications: boolean,
  hasCompletedPostgraduateEducationApplications: boolean
): boolean => {
  const completionByType: Record<degreeType, boolean> = {
    high: hasCompletedHighEducationApplications,
    additional: hasCompletedAdditionalEducationApplications,
    postgraduate: hasCompletedPostgraduateEducationApplications,
  };

  return getVisibleNotificationEducationTypes().some((type) => completionByType[type]);
};
